import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config';
import type { DuelClientMsg, DuelServerMsg } from '../../../shared/types';

export type DuelSockStatus = 'idle' | 'connecting' | 'open' | 'closed';

/**
 * Manages the duel WebSocket: authenticates via a query-string access token,
 * buffers sends until the socket opens, dispatches parsed server messages, and
 * auto-reconnects a few times on transient drops. The caller should (re)send a
 * `join` whenever status flips to `open` to (re)sync state.
 */
export function useDuelSocket(duelId: string, opts: {
  enabled: boolean;
  getToken: () => string | null;
  refresh: () => Promise<boolean>;
  onMessage: (msg: DuelServerMsg) => void;
}) {
  const { enabled, getToken, refresh, onMessage } = opts;
  const [status, setStatus] = useState<DuelSockStatus>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const queue = useRef<string[]>([]);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const send = useCallback((msg: DuelClientMsg) => {
    const data = JSON.stringify(msg);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
    else queue.current.push(data);
  }, []);

  useEffect(() => {
    if (!enabled || !duelId || !config.wsUrl) return;
    let cancelled = false;
    let retries = 0;
    let retryTimer: number | undefined;

    async function connect() {
      let token = getToken();
      if (!token) { await refresh(); token = getToken(); }
      if (!token || cancelled) return;
      setStatus('connecting');
      const url = `${config.wsUrl}?duel=${encodeURIComponent(duelId)}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        retries = 0;
        setStatus('open');
        for (const d of queue.current) ws.send(d);
        queue.current = [];
      };
      ws.onmessage = e => {
        try { onMessageRef.current(JSON.parse(e.data) as DuelServerMsg); } catch { /* ignore malformed */ }
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        wsRef.current = null;
        setStatus('closed');
        if (!cancelled && retries < 5) {
          retries += 1;
          retryTimer = window.setTimeout(connect, 500 * retries);
        }
      };
    }
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, duelId, getToken, refresh]);

  return { status, send };
}
